import { useCallback, useEffect, useRef, useState } from 'react'
import { useUser } from '@clerk/clerk-react'
import { workspaceService } from '../services/workspaceService'
import { useWorkspaceStore } from '../stores/workspaceStore'
import { oauthApi } from '../services/api'
import { getShopFromUrl } from '../lib/shopify-app-bridge'
import { useTheme } from '../theme/ThemeProvider'
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  CardFooter,
  CardHeader,
  Code,
  CodeBlock,
  Field,
  FlowDivider,
  FlowFine,
  FlowHelp,
  FlowIntro,
  FlowList,
  FlowScreen,
  Inline,
  LoadingState,
  Meta,
  SettingRow,
  Spacer,
  Stack,
  Stepper,
  ThemeToggle,
  TrustPill,
} from '../ds'

interface OnboardingFlowProps {
  onComplete: () => void
}

/**
 * Onboarding — five-step sign-up & Shopify-connect flow.
 *
 * Steps (adapted from the After Hours `web-app/Onboarding.html` prototype):
 *   0 Welcome    → confirm Clerk identity + company name        → createAccount
 *   1 Workspace  → name                                          → createWorkspace (shadow)
 *   2 Connect    → Shopify store domain                          → real OAuth redirect
 *   3 Tracking   → embed snippet (real, generated) + copy
 *   4 Meet Marva → shadow-mode intro                             → onComplete()
 *
 * Backend-wired: account, workspace, Shopify OAuth, embed script, completion.
 * Design-only (no onboarding backend yet): the "install automatically" one-click
 * app-embed — it advances and shows the real snippet to paste.
 */

const STEP_LABELS = ['Welcome', 'Workspace', 'Connect', 'Tracking', 'Meet Marva']
const STEPS = STEP_LABELS.length

const CheckIcon = () => (
  <svg viewBox="0 0 24 24"><path d="M5 12.5l4.5 4.5L19 7" /></svg>
)
const CopyIcon = () => (
  <svg viewBox="0 0 24 24"><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h10" /></svg>
)
const RetryIcon = () => (
  <svg viewBox="0 0 24 24"><path d="M21 12a9 9 0 1 1-2.64-6.36M21 3v6h-6" /></svg>
)

function slugify(s: string): string {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'workspace'
}

const OnboardingFlow = ({ onComplete }: OnboardingFlowProps) => {
  const { user } = useUser()
  const { mode, theme, cycleMode } = useTheme()
  const { currentAccount, currentWorkspace, setCurrentAccount } = useWorkspaceStore()

  const [step, setStep] = useState(0)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  // form state
  const [companyName, setCompanyName] = useState(currentAccount?.name || '')
  const [workspaceName, setWorkspaceName] = useState(currentWorkspace?.name || currentAccount?.name || '')
  const [storeDomain, setStoreDomain] = useState('')

  // tracking step
  const [snippet, setSnippet] = useState('')
  const [snippetLoading, setSnippetLoading] = useState(false)
  const [snippetError, setSnippetError] = useState(false)
  const [snippetAttempt, setSnippetAttempt] = useState(0)
  const snippetRequested = useRef(false)
  const [copied, setCopied] = useState(false)

  const email = user?.primaryEmailAddress?.emailAddress || ''
  const fullName =
    user?.fullName || [user?.firstName, user?.lastName].filter(Boolean).join(' ') || email || 'Your account'
  const avatarInitial = (user?.firstName?.[0] || email[0] || 'M').toUpperCase()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const emailVerified = (user?.primaryEmailAddress as any)?.verification?.status === 'verified'

  const go = useCallback((n: number) => {
    setError('')
    setStep(Math.max(0, Math.min(STEPS - 1, n)))
    window.scrollTo(0, 0)
  }, [])

  // Resume: prefill the shop and jump to the furthest step the user has reached.
  useEffect(() => {
    const shop = getShopFromUrl() || currentWorkspace?.shopify?.storeUrl
    if (shop) setStoreDomain(shop.replace('.myshopify.com', ''))

    const params = new URLSearchParams(window.location.search)
    if (params.get('installation') === 'success') {
      setStep(3) // returned from Shopify OAuth → straight to Tracking
      return
    }
    if (currentAccount && currentWorkspace && currentWorkspace.shopify?.storeUrl) setStep(3)
    else if (currentAccount && currentWorkspace) setStep(2)
    else if (currentAccount) setStep(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // STEP 0 — create the account from Clerk identity + company name.
  const handleAccount = async () => {
    if (currentAccount) return go(1)
    if (!companyName.trim()) return setError('Please enter your company name.')
    if (!email) return setError('We couldn’t read your email from Clerk — try signing in again.')

    setBusy(true)
    setError('')
    try {
      const account = await workspaceService.createAccount({
        name: companyName.trim(),
        email,
        plan: 'pro',
        clerkUserId: user?.id,
      })
      setCurrentAccount(account)
      if (!workspaceName.trim()) setWorkspaceName(companyName.trim())
      go(1)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create account.')
    } finally {
      setBusy(false)
    }
  }

  // STEP 1 — create the first workspace (starts in shadow mode).
  const handleWorkspace = async () => {
    if (currentWorkspace) return go(2)
    if (!workspaceName.trim()) return setError('Please name your workspace.')
    if (!currentAccount) return setError('Account not ready — go back a step.')

    setBusy(true)
    setError('')
    try {
      const created = await workspaceService.createWorkspace(
        currentAccount.id,
        { name: workspaceName.trim(), mode: 'shadow' },
        {
          clerkUserId: user?.id,
          name: fullName,
          firstName: user?.firstName || undefined,
          lastName: user?.lastName || undefined,
        }
      )
      // `null`: the session that issued this create ended while it was in flight — no step 2.
      if (!created) return
      go(2)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create workspace.')
    } finally {
      setBusy(false)
    }
  }

  // STEP 2 — kick off the real Shopify OAuth redirect.
  const handleShopify = async () => {
    if (!storeDomain.trim()) return setError('Enter your store name.')
    setBusy(true)
    setError('')
    try {
      const normalized = storeDomain.includes('.myshopify.com') ? storeDomain : `${storeDomain}.myshopify.com`
      const res = await oauthApi.getConnectUrl(normalized, currentWorkspace?.id)
      if (res.success && res.data?.authorizationUrl) {
        window.location.href = res.data.authorizationUrl
      } else {
        setError(res.error || 'Failed to start the Shopify connection.')
        setBusy(false)
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to connect Shopify.')
      setBusy(false)
    }
    // on success we navigate away — leave busy=true
  }

  // STEP 3 — generate the real embed snippet once we land on Tracking.
  // No hardcoded fallback: a made-up snippet would silently break tracking on
  // the merchant's storefront, so a failed generate shows an error + retry.
  useEffect(() => {
    if (step !== 3 || snippetRequested.current) return
    if (!currentAccount || !currentWorkspace) return
    snippetRequested.current = true
    setSnippetLoading(true)
    setSnippetError(false)
    workspaceService
      .generateEmbedScript(currentAccount.id, currentWorkspace.id)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((data: any) => {
        const s = typeof data === 'string' ? data : data?.script || ''
        setSnippet(s)
        setSnippetError(!s)
      })
      .catch(() => setSnippetError(true))
      .finally(() => setSnippetLoading(false))
  }, [step, currentAccount, currentWorkspace, snippetAttempt])

  const retrySnippet = () => {
    snippetRequested.current = false
    setSnippetAttempt((a) => a + 1)
  }
  const snippetText = snippet

  const copySnippet = () => {
    const done = () => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1800)
    }
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(snippetText).then(done).catch(done)
    else done()
  }

  const wsSlug = slugify(workspaceName || 'workspace')
  const wsInitial = (workspaceName.trim()[0] || 'W').toUpperCase()

  return (
    <FlowScreen
      aside={
        <>
          <FlowHelp>
            Need a hand? <a href="mailto:hello@marva.co">hello@marva.co</a>
          </FlowHelp>
          <ThemeToggle mode={mode} theme={theme} onChange={() => cycleMode()} />
        </>
      }
    >
      <Stepper steps={STEP_LABELS} current={step} label="Setup progress" />

      {error && <Alert tone="danger" live="assertive">{error}</Alert>}

      {/* STEP 1: welcome */}
      {step === 0 && (
        <Card padding="lg">
          <FlowIntro orb title={<>Welcome to <em className="mv-em">Marva.</em></>}>
            Let’s set up your autonomous marketing agent. We’ve pulled your details from your account —
            confirm them to get started.
          </FlowIntro>
          <Stack>
            <Card tone="recessed" padding="sm">
              <Inline grow>
                <Avatar name={avatarInitial} size="sm" tone="gold" label={false} />
                <SettingRow
                  title={fullName}
                  description={email}
                  aside={emailVerified ? <Badge tone="sage">Verified</Badge> : undefined}
                />
              </Inline>
            </Card>
            <Field
              label="Company name"
              value={companyName}
              onChange={(e) => setCompanyName((e.target as HTMLInputElement).value)}
              placeholder="Enter your company name"
            />
            <Button block size="lg" onClick={handleAccount} busy={busy} busyLabel="Creating account…">
              Create account &amp; continue
            </Button>
          </Stack>
        </Card>
      )}

      {/* STEP 2: workspace */}
      {step === 1 && (
        <Card padding="lg">
          <FlowIntro
            icon={<svg viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 9h18M8 4v5" /></svg>}
            title={<>Create your <em className="mv-em">workspace.</em></>}
          >
            A workspace is one store Marva runs — its memory, voice and limits live here. Name it to get started;
            you can add more workspaces later.
          </FlowIntro>
          <Stack>
            <Card tone="recessed" padding="sm">
              <Inline grow>
                <Avatar name={wsInitial} shape="square" tone="gold" label={false} />
                <SettingRow
                  title={workspaceName || 'Workspace'}
                  description={<Meta>{wsSlug}</Meta>}
                />
              </Inline>
            </Card>
            <Field
              label="Workspace name"
              value={workspaceName}
              onChange={(e) => setWorkspaceName((e.target as HTMLInputElement).value)}
              placeholder="e.g. Norden Goods"
            />
            <Button block size="lg" onClick={handleWorkspace} busy={busy} busyLabel="Creating workspace…">
              Create workspace
            </Button>
          </Stack>
          <CardFooter>
            <Button variant="ghost" size="sm" onClick={() => go(0)}>Back</Button>
          </CardFooter>
        </Card>
      )}

      {/* STEP 3: connect Shopify */}
      {step === 2 && (
        <Card padding="lg">
          <FlowIntro
            icon={<svg viewBox="0 0 24 24"><path d="M4 7h16l-1.5 11H5.5zM4 7L3 3H1M8 11h8" /></svg>}
            title={<>Connect your <em className="mv-em">Shopify store.</em></>}
          >
            Marva reads your orders, products, customers and live behaviour to build a memory of every shopper.
            Read-only to start — she sends nothing yet.
          </FlowIntro>
          <Stack>
            <Field
              label="Your store"
              suffix=".myshopify.com"
              value={storeDomain}
              onChange={(e) => setStoreDomain((e.target as HTMLInputElement).value)}
              onKeyDown={(e) => e.key === 'Enter' && handleShopify()}
            />
            <Button block size="lg" onClick={handleShopify} busy={busy} busyLabel="Connecting…">
              Connect with Shopify
            </Button>
            <FlowList
              variant="check"
              label="What Marva can do"
              items={[
                { key: 'read', icon: <CheckIcon />, title: 'Read orders, products & customers' },
                { key: 'live', icon: <CheckIcon />, title: 'See live storefront behaviour' },
                { key: 'quiet', icon: <CheckIcon />, title: 'Nothing sent to customers until you say so' },
              ]}
            />
          </Stack>
          <CardFooter>
            <Button variant="ghost" size="sm" onClick={() => go(1)}>Back</Button>
            <Spacer />
            <Button variant="ghost" size="sm" onClick={() => go(3)}>Skip for now</Button>
          </CardFooter>
        </Card>
      )}

      {/* STEP 4: tracking snippet */}
      {step === 3 && (
        <Card padding="lg">
          <FlowIntro
            icon={<svg viewBox="0 0 24 24"><path d="M8 6l-5 6 5 6M16 6l5 6-5 6M13 4l-2 16" /></svg>}
            title={<>Add the <em className="mv-em">tracking script.</em></>}
          >
            This is what lets Marva notice the things a great shopkeeper would — who’s browsing, who’s hesitating,
            who’s about to leave.
          </FlowIntro>
          <Stack>
            <Card tone="recessed" padding="sm">
              <CardHeader
                title="Your install snippet"
                titleAs="h2"
                aside={
                  snippetError && !snippetLoading ? (
                    <Button variant="ghost" size="sm" icon={<RetryIcon />} onClick={retrySnippet}>Retry</Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={copied ? <CheckIcon /> : <CopyIcon />}
                      onClick={copySnippet}
                      disabled={snippetLoading || !snippetText}
                    >
                      {copied ? 'Copied' : 'Copy'}
                    </Button>
                  )
                }
              />
              {snippetLoading ? (
                <LoadingState caption="Generating your snippet…" />
              ) : snippetError ? (
                <Alert tone="danger" live="polite">We couldn’t generate your snippet just now.</Alert>
              ) : (
                <CodeBlock>{snippetText}</CodeBlock>
              )}
            </Card>
            <FlowFine>
              Paste it just before <Code>&lt;/head&gt;</Code> in your theme — or let us add it for you.
            </FlowFine>
            <Button block size="lg" onClick={() => go(4)}>Install automatically &amp; continue</Button>
            <FlowDivider />
            <FlowList
              items={[
                { key: 'embed', icon: <CheckIcon />, title: 'One-click install', description: 'We add it to your theme automatically via the Shopify app embed.' },
                {
                  key: 'speed',
                  icon: <svg viewBox="0 0 24 24"><path d="M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM12 8v4l3 2" /></svg>,
                  title: 'No speed hit',
                  description: 'Loads async, under 8kb. It won’t slow your store.',
                },
                {
                  key: 'privacy',
                  icon: <svg viewBox="0 0 24 24"><path d="M5 11h14v9H5zM8 11V8a4 4 0 0 1 8 0v3" /></svg>,
                  title: 'Privacy-first',
                  description: 'Respects consent and never captures payment details.',
                },
              ]}
            />
          </Stack>
          <CardFooter>
            <Button variant="ghost" size="sm" onClick={() => go(2)}>Back</Button>
            <Spacer />
            <Button variant="ghost" size="sm" onClick={() => go(4)}>I’ve pasted it — continue</Button>
          </CardFooter>
        </Card>
      )}

      {/* STEP 5: meet Marva / shadow */}
      {step === 4 && (
        <Card padding="lg">
          <FlowIntro
            orb
            badge={<TrustPill level="shadow" label="Starting in Shadow mode" />}
            title={<>Marva’s on the floor — <em className="mv-em">watching first.</em></>}
          >
            For the next while she’ll watch and draft, but send nothing. You’ll see exactly what she{' '}
            <em className="mv-em">would</em> have done. When you’ve seen enough, promote her a rung. That’s how
            trust gets earned.
          </FlowIntro>
          <Button block size="lg" onClick={onComplete}>Go to my dashboard</Button>
        </Card>
      )}
    </FlowScreen>
  )
}

export default OnboardingFlow
